import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Box } from '@twilio-paste/box';
import { Grid, Column } from '@twilio-paste/grid';
import { Anchor } from '@twilio-paste/anchor';
import { Button } from '@twilio-paste/button';
import { Card } from '@twilio-paste/card';
import { Heading } from '@twilio-paste/heading';
import { Paragraph } from '@twilio-paste/paragraph';
import { Text } from '@twilio-paste/text';
import { SidebarCategoryRoutes } from '../../../constants';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { SiteLink } from '../../../components/SiteLink';
import Changelog from '@twilio-paste/grid/CHANGELOG.md';
import { TwilioIcon } from '../../../components/icons/TwilioIcon';
import { TWILIO_RED } from '../../../constants';
export const pageQuery = graphql`
  {
    allPasteLayout(filter: {name: {eq: "@twilio-paste/grid"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/layout/grid/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name="Grid" categoryRoute={SidebarCategoryRoutes.LAYOUT} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/layout/grid" storybookUrl="/?path=/story/layout-grid--grid-12-column-and-gutter-support" abstractUrl="https://share.goabstract.com/3e7528f5-7981-421c-9308-e06d2f341c70?" data={props.data.allPasteLayout.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <Paragraph mdxType="Paragraph">{props.pageContext.frontmatter.description}</Paragraph>
        <h3>{`About Grid`}</h3>
        <p>{`Grid creates organized and consistent product layouts, by providing a standard for positioning elements. Grid speeds up the designer-to-developer workflow by improving the consistency of design implementations.
Use a Grid to layout and align content by applying standard spacing between elements on a page or within a component.`}</p>
        <p>{`The standard desktop grid has 24-pixel gutters and can support up to 12 columns. The outside gutters are removed from the overall width of the grid.`}</p>
        <p>{`Grid is built using the `}<a parentName="p" {...{
            "href": "/layout/flex"
          }}>{`Flex component`}</a>{` and is responsive. Grid column widths grow and shrink based on the parent element width.`}</p>
        <h2>{`Examples`}</h2>
        <h3>{`Equal column widths`}</h3>
        <p>{`Use Column, which is a child component of Grid, to lay out equal-sized content, such as a feed of cards.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Card,
          Heading,
          Paragraph
        }} language="jsx" mdxType="LivePreview">
  {`<Grid gutter="space30">
  <Column>
    <Card padding="space70">
      <Heading as="h4" variant="heading40">Instant Provisioning</Heading>
      <Paragraph>The API and the Console allow you to search and provision local, toll-free, or mobile numbers by capability, geography, prefix or keyword.</Paragraph>
    </Card>
  </Column>
  <Column>
    <Card padding="space70">
      <Heading as="h4" variant="heading40">Vast Inventory</Heading>
      <Paragraph>Twilio maintains phone number inventory at a rate of 50 times the quantity provisioned daily for over 600 area codes and prefixes.</Paragraph>
    </Card>
  </Column>
  <Column>
    <Card padding="space70">
      <Heading as="h4" variant="heading40">Quality Screening</Heading>
      <Paragraph>Every phone number in the inventory passes a rigorous 60-day screening process to verify capabilities and test for unwanted call traffic.</Paragraph>
    </Card>
  </Column>
</Grid>`}
        </LivePreview>
        <h3>{`Unequal column widths, using column span`}</h3>
        <p>{`Use the `}<inlineCode parentName="p">{`span`}</inlineCode>{` prop to allow content to span multiple columns. `}<inlineCode parentName="p">{`Span`}</inlineCode>{` defines how many columns you want the width of your content to take up out of a 12-column grid. The value of `}<inlineCode parentName="p">{`span`}</inlineCode>{` becomes the
numerator while 12 is the denominator, which gives us a percentage width value for each column. The default `}<inlineCode parentName="p">{`span`}</inlineCode>{` is 1 column width.`}</p>
        <p>{`In our example we're setting one column to equal 8 of the 12 columns (`}<inlineCode parentName="p">{`span={8}`}</inlineCode>{`), and the other to be 4 of the 12 columns (`}<inlineCode parentName="p">{`span={4}`}</inlineCode>{`).`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Card,
          Heading,
          Paragraph,
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Grid gutter="space30">
  <Column span={8}>
    <Heading as="h4" variant="heading40">Getting Started with Parse</Heading>
    <Paragraph>
      Use SendGrid to make your email a two-way street. The Parse Webhook is a powerful tool that can take incoming email for your domain or subdomain and direct it to your application. The possibilities of this are endless!
    </Paragraph>
    <Heading as="h4" variant="heading40">Your Email Funnel</Heading>
    <Paragraph>
      Expert Insights analyzes each layer of your email funnel to determine leakage points (such as blocked emails) and opportunities to improve deliverability and engagement.
    </Paragraph>
  </Column>
  <Column span={4}>
    <Card padding="space70">
      <Heading as="h4" variant="heading40">Expert Insights</Heading>
      <Paragraph>Send better email with Expert Insights. Our detailed monthly reports with expert recommendations will give you deeper visibility into the health and performance of your email program.</Paragraph>
      <Button>Add to Plan</Button>
    </Card>
  </Column>
</Grid>`}
        </LivePreview>
        <Callout mdxType="Callout">
  <CalloutTitle as="h4" variant="heading40" mdxType="CalloutTitle">
    An important note about span and offset props
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    When using <strong>span</strong> or <strong>offset</strong>, base your layout off a 12-column grid. For example, if
    you want to have two columns with one spanning twice as much as the other, then one column would span=8 and the
    other would span=4.
  </CalloutText>
        </Callout>
        <h3>{`Offset column`}</h3>
        <p>{`Use `}<inlineCode parentName="p">{`offset`}</inlineCode>{` to push a column to the right by a certain number of columns. For example, a layout with centered columns.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Card,
          Button,
          Heading,
          Paragraph,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Grid gutter="space30">
  <Column span={8} offset={2}>
    <Card padding="space70">
      <Text as="div" textAlign="center">
        <Heading as="h4" variant="heading40">Create your first Signup Form</Heading>
        <Paragraph>Create and share a form to capture new signups and add them directly to your Contacts. You’ll grow your list the right way, knowing each new contact has opted in to receive your content.</Paragraph>
        <Button>Create a Signup Form</Button>
      </Text>
    </Card>
  </Column>
</Grid>`}
        </LivePreview>
        <h3>{`Equal column heights`}</h3>
        <p>{`Set `}<inlineCode parentName="p">{`equalColumnHeights`}</inlineCode>{` on a grid container and all columns in a single row will share the height of the tallest column.`}</p>
        <p>{`This can be useful when trying to visually align Cards where their content is variable.`}</p>
        <p>{`When columns have equal heights, a single child element will stretch to the height of the column. You may still need to control the width of that child though.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Card,
          Button,
          Heading,
          Paragraph,
          Box
        }} language="jsx" mdxType="LivePreview">
  {`<Grid gutter="space30" equalColumnHeights>
  <Column span={4}>
    <Card padding="space70">
      <Heading as="h2" variant="heading20">
        Why use Paste?
      </Heading>
      <Paragraph marginBottom="space0">
        Paste helps you rapidly prototype, and ship great, inclusive customer experiences. It makes it easy to
        do the right thing, cheaply.
      </Paragraph>
    </Card>
  </Column>
  <Column span={4}>
    <Card padding="space70">
      <Heading as="h2" variant="heading20">
        Inclusive by default
      </Heading>
      <Paragraph marginBottom="space0">
        Paste meets WCAG 2.1 AA standards in the design and development of our components, making it even easier
        to build accessibly.
      </Paragraph>
    </Card>
  </Column>
  <Column span={4}>
    <Card padding="space70">
      <Heading as="h2" variant="heading20">
        Get up and running
      </Heading>
      <Paragraph marginBottom="space0">
        Whether you’re a designer or developer, setting up Paste is easy and only takes a few minutes!
      </Paragraph>
    </Card>
  </Column>
</Grid>`}
        </LivePreview>
        <h3>{`Stacked columns in a vertical layout`}</h3>
        <p>{`Use the `}<inlineCode parentName="p">{`vertical`}</inlineCode>{` prop to stack columns vertically instead of horizontally. The `}<inlineCode parentName="p">{`vertical`}</inlineCode>{` prop can work along with theme breakpoints,
so your grid can be vertical on different viewports. For example to have your grid vertical on mobile and not tablet or desktop viewports,
use `}<inlineCode parentName="p">{`vertical={[true, false, false]}`}</inlineCode>{`. For a visual example, `}<a parentName="p" {...{
            "href": "https://main--5e53448165911c0022e68c74.chromatic.com/?path=/story/layout-grid--grid-2-column-responsive-stack"
          }}>{`see the Storybook example`}</a>{`
and resize the browser.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Button,
          Box,
          Card,
          Heading,
          Paragraph
        }} language="jsx" mdxType="LivePreview">
  {`<Grid gutter="space30" vertical>
  <Column>
    <Box marginTop="space30">
      <Card padding="space70">
        <Heading as="h4" variant="heading40">Web API</Heading>
        <Paragraph>The fastest, most flexible way to send email using languages like Node.js, Ruby, C#, and more.</Paragraph>
        <Button>Choose</Button>
      </Card>
    </Box>
  </Column>
  <Column>
    <Box marginTop="space30">
      <Card padding="space70">
        <Heading as="h4" variant="heading40">SMTP Relay</Heading>
        <Paragraph>The easiest way to send email. It only requires modifying your application's SMTP configuration.</Paragraph>
        <Button>Choose</Button>
      </Card>
    </Box>
  </Column>
  <Column>
    <Box marginTop="space30">
      <Card padding="space70">
        <Heading as="h4" variant="heading40">What's new at Twilio</Heading>
        <Paragraph>Check out our newest product releases on the Twilio Changelog.</Paragraph>
        <Button>Changelog</Button>
      </Card>
    </Box>
  </Column>
</Grid>`}
        </LivePreview>
        <h3>{`Nested Grids`}</h3>
        <p>{`A Grid can be nested inside another Grid. For example, you can split a column into multiple smaller columns in order to stack the outer Grid in a responsive view but not the inner Grid.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Box,
          Card,
          Heading,
          Paragraph
        }} language="jsx" mdxType="LivePreview">
  {`<Grid vertical>
  <Column>
    <Grid gutter="space30">
      <Column>
        <Card padding="space70">
          <Heading as="h4" variant="heading40">Getting Started</Heading>
          <Paragraph>To get started with Phone Numbers, check out our Getting Started Guide.</Paragraph>
        </Card>
      </Column>
      <Column>
        <Card padding="space70">
          <Heading as="h4" variant="heading40">About Web API</Heading>
          <Paragraph>The fastest, most flexible way to send email using languages like Node.js, Ruby, C#, and more.</Paragraph>
        </Card>
      </Column>
    </Grid>
  </Column>
  <Column>
    <Box marginTop="space60" marginBottom="space60">
      <Card padding="space70">
        <Heading as="h4" variant="heading40">SMTP Relay</Heading>
        <Paragraph>The easiest way to send email. It only requires modifying your application's SMTP configuration.</Paragraph>
      </Card>
    </Box>
  </Column>
  <Column>
    <Grid gutter="space30">
      <Column>
        <Card padding="space70">
          <Heading as="h4" variant="heading40">About Phone Numbers</Heading>
          <Paragraph>Programmable phone numbers are a core part of Twilio’s platform, enabling you to receive SMS, MMS, and phone calls.</Paragraph>
        </Card>
      </Column>
      <Column>
        <Card padding="space70">
          <Heading as="h4" variant="heading40">Messaging Services</Heading>
          <Paragraph>Messaging Services allow you to organize your messages and enable specific features for groups of messages.</Paragraph>
        </Card>
      </Column>
    </Grid>
  </Column>
</Grid>`}
        </LivePreview>
        <h3>{`Responsive columns`}</h3>
        <p>{`Grid column `}<inlineCode parentName="p">{`span`}</inlineCode>{` and `}<inlineCode parentName="p">{`offset`}</inlineCode>{` props can be changed based on the theme breakpoints. Both `}<inlineCode parentName="p">{`span`}</inlineCode>{` and `}<inlineCode parentName="p">{`offset`}</inlineCode>{` can accept responsive values, for example `}<inlineCode parentName="p">{`span={[6, 8, 2] }`}</inlineCode>{`.
This would set a columns `}<inlineCode parentName="p">{`span`}</inlineCode>{` to be 6 columns wide on mobile, 8 columns wide on tablet, and 2 columns wide on desktop. The `}<inlineCode parentName="p">{`offset`}</inlineCode>{` prop works in a similar fashion.
For a visual example, `}<a parentName="p" {...{
            "href": "https://main--5e53448165911c0022e68c74.chromatic.com/?path=/story/layout-grid--grid-2-column-responsive"
          }}>{`see the Storybook example`}</a>{` and resize the browser.`}</p>
        <h2>{`Composition Notes`}</h2>
        <p>{`Grid can be used to compose various types of layouts ranging from whole application pages and views down to individual component layouts.`}</p>
        <Callout mdxType="Callout">
  <CalloutTitle as="h4" variant="heading40" mdxType="CalloutTitle">
    Gutter sizing
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    For spacing content in a layout wider than 1440 pixels, we recommend using a 24-pixel gutter. Use $space-40 to get a
    24-pixel gutter.
  </CalloutText>
  <CalloutText mdxType="CalloutText">
    For spacing content in a layout narrower than 1440 pixels, we recommend using a 16-pixel gutter. Use $space-30 to
    get a 16-pixel gutter.
  </CalloutText>
        </Callout>
        <h3>{`Basic page layout`}</h3>
        <p>{`Let's look at a common use case where we need to layout a page with a sidebar and a grid within the page content area.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Button,
          Box,
          Card,
          Heading,
          Paragraph,
          Anchor,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Card padding="space70">
  <Grid gutter="space30">
    <Column span={3}>
      <Box backgroundColor="colorBackground" padding="space70" borderRadius="borderRadius20">
        <Heading as="h4" variant="heading40">Dashboard</Heading>
        <Box marginBottom="space20">
          <Anchor href="#">Billing</Anchor>
        </Box>
        <Box marginBottom="space20">
          <Anchor href="#">Usage</Anchor>
        </Box>
        <Box marginBottom="space20">
          <Anchor href="#">Settings</Anchor>
        </Box>
      </Box>
    </Column>
    <Column span={9}>
      <Box marginBottom="space50">
        <Card>
          <Heading as="h4" variant="heading40">Project Info</Heading>
          <Text as="p" marginBottom="space0" color="colorTextWeak">BALANCE</Text>
          <Text as="p" marginBottom="space50" color="colorTextSuccess" fontWeight="fontWeightBold">$33.28</Text>
          <Text as="p" marginBottom="space0" color="colorTextWeak">REFERRAL PROGRAM</Text>
          <Text as="p" marginBottom="space0">Refer your network to Twilio — give $10, get $10.</Text>
        </Card>
      </Box>
      <Grid gutter="space30">
        <Column>
          <Card>
            <Heading as="h4" variant="heading40">What's new at Twilio</Heading>
            <Paragraph>Check out our newest product releases on the Twilio Changelog.</Paragraph>
            <Button>Changelog</Button>
          </Card>
        </Column>
        <Column>
          <Card>
            <Heading as="h4" variant="heading40">Twilio Docs</Heading>
            <Paragraph>Dig into our API reference documentation and quickstarts. You're covered with everything from SDKs in your favorite languages to sample apps for web, iOS, and Android.</Paragraph>
            <Button variant="secondary">Explore the Docs</Button>
          </Card>
        </Column>
      </Grid>
    </Column>
  </Grid>
</Card>`}
        </LivePreview>
        <h3>{`Responsive page layout`}</h3>
        <p>{`Grid can also be used for responsive layouts.`}</p>
        <ul>
          <li parentName="ul">{`You can determine what size columns should be based on the breakpoints set on a theme. `}<a parentName="li" {...{
              "href": "https://main--5e53448165911c0022e68c74.chromatic.com/?path=/story/layout-grid--grid-2-column-responsive"
            }}>{`See the Storybook example`}</a></li>
          <li parentName="ul">{`Grid columns can also stack based on theme breakpoints. `}<a parentName="li" {...{
              "href": "https://main--5e53448165911c0022e68c74.chromatic.com/?path=/story/layout-grid--grid-2-column-responsive-stack"
            }}>{`See the Storybook example`}</a></li>
        </ul>
        <h3>{`Component layout`}</h3>
        <p>{`Grids can also be used for layouts within other components. For example, we can set up our card content to include 2 columns.`}</p>
        <LivePreview scope={{
          Grid,
          Column,
          Card,
          Heading,
          Paragraph,
          TwilioIcon,
          TWILIO_RED,
          Button
        }} language="jsx" mdxType="LivePreview">
  {`<Card padding="space70">
  <Grid gutter="space30">
    <Column span={2}>
      <TwilioIcon color={TWILIO_RED} display="block" size={65} />
    </Column>
    <Column span={10}>
      <Heading as="h4" variant="heading40">What's new at Twilio</Heading>
      <Paragraph>Check out our newest product releases on the Twilio Changelog.</Paragraph>
      <Button>Changelog</Button>
    </Column>
  </Grid>
</Card>`}
        </LivePreview>
        <h2>{`When to use Grid`}</h2>
        <p>{`Use Grid whenever you need to layout or align content on a page or within a component. Grid should also be used for responsive page layouts.
Since the grid is sized to the full width of its parent container, you may not want to use it for fixed width columns in a variable width container.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Base the Column span and offset on a 12-column layout." mdxType="Do">
    <Box padding="space60" mdxType="Box">
      <Grid gutter="space30" mdxType="Grid">
        <Column span={4} mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">4 Column Card</Paragraph>
          </Card>
        </Column>
        <Column span={8} mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">8 Column Card</Paragraph>
          </Card>
        </Column>
      </Grid>
    </Box>
  </Do>
  <Dont title="Don't" body="Don’t use more than 12 columns within Grid." mdxType="Dont">
    <Box padding="space60" mdxType="Box">
      <Grid gutter="space30" mdxType="Grid">
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
      </Grid>
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use Grid to align content to the right or left side of a page layout." mdxType="Do">
    <Box padding="space60" mdxType="Box">
      <Grid mdxType="Grid">
        <Column span={4} offset={8} mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
      </Grid>
    </Box>
  </Do>
  <Dont title="Don't" body="Don’t use Grid with only one full-width column of content. This causes unnecessary elements. Box is what you’re looking for here." mdxType="Dont">
    <Box padding="space60" mdxType="Box">
      <Grid gutter="space30" mdxType="Grid">
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">
              Unnecessary HTML elements. Use <SiteLink to="/primitives/box" mdxType="SiteLink">Box</SiteLink> instead.
            </Paragraph>
          </Card>
        </Column>
      </Grid>
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use Grid to lay out UI elements into consistent columns." mdxType="Do">
    <Box padding="space60" mdxType="Box">
      <Grid gutter="space30" mdxType="Grid">
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
      </Grid>
      <Box marginTop="space50" mdxType="Box">
        <Grid gutter="space30" mdxType="Grid">
          <Column mdxType="Column">
            <Card mdxType="Card">
              <Paragraph mdxType="Paragraph">Card content</Paragraph>
            </Card>
          </Column>
          <Column mdxType="Column">
            <Card mdxType="Card">
              <Paragraph mdxType="Paragraph">Card content</Paragraph>
            </Card>
          </Column>
        </Grid>
      </Box>
    </Box>
  </Do>
  <Dont title="Don't" body="Don’t let columns grow too small. Gutters should never be wider than columns." mdxType="Dont">
    <Box padding="space60" mdxType="Box">
      <Grid gutter="space120" mdxType="Grid">
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
        <Column mdxType="Column">
          <Card mdxType="Card">
            <Paragraph mdxType="Paragraph">Card content</Paragraph>
          </Card>
        </Column>
      </Grid>
    </Box>
  </Dont>
        </DoDont>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/grid - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Grid, Column} from '@twilio-paste/grid';

const Component = () => (
  <Grid>
    <Column>Foo</Column>
  </Grid>
);
`}</code></pre>
        <h4>{`Grid Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`equalColumnHeights?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the height of columns in a row to be equal.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`gutter?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ResponsiveValue<keyof ThemeShape['space']>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the spacing on both sides of a Column. Use a spacing token that represents half of the total space you need between 2 Columns.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`vertical?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean, ResponsiveValue<boolean>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Stacks the Columns`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
          </tbody>
        </table>
        <h4>{`Column Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`span?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`number, ResponsiveValue<number>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the width of Columns (how many columns a column can span)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`offset?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`number, ResponsiveValue<number>`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Sets the left margin of a Column (how many columns a column is offset)`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
          </tbody>
        </table>
        <h3>{`Sketch`}</h3>
        <p>{`Add `}<a parentName="p" {...{
            "href": "https://share.goabstract.com/3e7528f5-7981-421c-9308-e06d2f341c70"
          }}>{`Grid symbols from the “Paste components” project`}</a>{` to your own artboards to help you align content.`}</p>
        <p>{`Additionally, each Paste container component (`}<em parentName="p">{`e.g.`}</em>{`, Card, Modal) symbol in Sketch has an invisible grid symbol named “📐 Grid” that you can override with visible grid symbols.`}</p>
        <p>{`We use grid symbols instead of Sketch Layout Settings for two reasons:`}</p>
        <ol>
          <li parentName="ol">{`Resizing a symbol better represents the behavior of a fluid grid in a responsive layout. You might be designing for multiple screen widths in a single Sketch file. Sketch Layout Settings, however, restricts you to a single setting of grid width, column width, gutter width, and number of columns for each file.`}</li>
          <li parentName="ol">{`Symbols allow you to place a grid within a component or container on an artboard, whereas Sketch Layout Settings can be applied only to a whole artboard.`}</li>
        </ol>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      